//Node Modules
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import _ from "lodash";
import { Duration } from "luxon";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import {
	getAlarmGroupDetails,
	liveEventByDeviceBasic,
	archiveEventByDeviceBasic,
} from "../../../graphql/queries-custom";
import { onUpdateFlowstop } from "../../../graphql/subscriptions-custom";

//BinaryForge Components
import { PageHeader } from "../../../components/layout";
import { Note, PillItem } from "../../../components/helpers";

//3rd Party Components
import { ProgressSpinner } from "primereact/progressspinner";

//Atoms
import { deviceSensorAlarmListAtom } from "../../../atoms/alarm";

//Helpers
import { getUiDateTime } from "../../../helpers/DateUtils";
import { useDeviceOfflineState } from "../../../helpers/useDeviceOfflineState";
import { getValveSummary } from "../../../helpers/Device";
import { inputDeviceOptions, outputDeviceOptions, useSensorActuatorLabels } from "../../../config/device";

//Other
import { nav } from "../../../config/navigation";

const RECENT_EVENTS_LIMIT = 10;

const parseReportedState = (value) => {
	if (!value) return {};
	if (typeof value === "object") return value;
	try {
		return JSON.parse(value) ?? {};
	} catch {
		return {};
	}
};

const getDeviceValveSummary = (device) => getValveSummary(parseReportedState(device?.reported_state));

//Devices for one side of the group, dropping orphaned join records
const getDevices = (groupData, side) => (groupData?.[side]?.items ?? []).map((item) => item?.device).filter(Boolean);

const getDeviceMeta = (device, options, typeField) => {
	const deviceObj = _.find(options, { value: device?.[typeField]?.[0] ?? null });
	return {
		icon: deviceObj?.icon ?? "pi pi-question",
		typeLabel: deviceObj?.value ? deviceObj.label : null,
	};
};

const DeviceItem = ({ device, icon, typeLabel, offline, pills }) => {
	return (
		<div className={`deviceItem flex aItems-center jContent-spaceBetween gap-small ${offline ? "offline" : ""}`}>
			<div className="deviceItem-info flex aItems-center gap-xsmall">
				<i className={icon} />
				<div className="flexVert">
					<Link to={`${nav.device.base}/${device.id}`}>{device.name}</Link>
					<span className="fontSize-small">
						{device.zone?.label || "N/A"}
						{typeLabel && ` | ${typeLabel}`}
					</span>
				</div>
			</div>
			<div className="flex aItems-center gap-xsmall">
				{pills.map((pill) => (
					<PillItem
						key={pill.value}
						type={pill.icon ? "full" : "text"}
						value={pill.value}
						icon={pill.icon}
						wrapperStyle={pill.style}
						tooltip={pill.tooltip}
					/>
				))}
			</div>
		</div>
	);
};

const AlarmGroupDetails = () => {
	//Hooks
	const { t } = useTranslation();
	const { groupId } = useLoaderData();

	//Recoil
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const getOfflineState = useDeviceOfflineState();

	//Local State
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [groupInfo, setGroupInfo] = useState(null);
	const [recentEvents, setRecentEvents] = useState([]);

	//On Page Load
	useEffect(() => {
		handleGetGroup();
		// Re-run on groupId change so navigating between groups refetches the new group
	}, [groupId]);

	//Re-fetch when the live alarm list changes (alarm raised or cleared) so alert, valve and event data stay current
	const skipFirstAlarmRefresh = useRef(true);
	useEffect(() => {
		if (skipFirstAlarmRefresh.current) {
			skipFirstAlarmRefresh.current = false;
			return;
		}
		handleGetGroup();
	}, [activeAlarms]);

	//Subscribe to each group device's updates so valve state and presence stay live.
	//Keyed on device ids (not groupInfo) to avoid re-subscribing on every refetch.
	const deviceIdsKey = _.uniq(
		[...getDevices(groupInfo, "input"), ...getDevices(groupInfo, "output")].map((device) => device.id),
	)
		.sort()
		.join("|");
	useEffect(() => {
		if (!deviceIdsKey) return;

		const subscriptions = deviceIdsKey.split("|").map((deviceId) =>
			API.graphql(graphqlOperation(onUpdateFlowstop, { id: deviceId })).subscribe({
				next: ({ value }) => {
					const updatedDevice = value?.data?.onUpdateDeviceById;
					if (updatedDevice?.id) applyDeviceUpdate(updatedDevice);
				},
				error: (error) => console.warn("Group device update subscription error", error),
			}),
		);

		return () => subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
	}, [deviceIdsKey]);

	//Patch the updated device's reported state into the group data without a refetch
	const applyDeviceUpdate = (updatedDevice) => {
		setGroupInfo((currentGroup) => {
			if (!currentGroup) return currentGroup;

			const patchSide = (side) => ({
				...side,
				items: (side?.items ?? []).map((item) =>
					item?.device?.id === updatedDevice.id
						? {
								...item,
								device: {
									...item.device,
									reported_state: updatedDevice.reported_state,
									updatedAt: new Date().toISOString(),
								},
							}
						: item,
				),
			});

			return { ...currentGroup, input: patchSide(currentGroup.input), output: patchSide(currentGroup.output) };
		});
	};

	//Get the group data, then the recent events for its devices.
	//Sequence counter drops out-of-order responses when alarm changes trigger overlapping fetches.
	const fetchSeq = useRef(0);
	const handleGetGroup = async () => {
		const seq = ++fetchSeq.current;
		try {
			const {
				data: { getAlarmGroup: groupData },
			} = await API.graphql(graphqlOperation(getAlarmGroupDetails, { id: groupId }));
			if (seq !== fetchSeq.current) return;

			setGroupInfo(groupData);
			if (groupData) await handleGetRecentEvents(groupData, seq);
		} catch (err) {
			if (seq !== fetchSeq.current) return;
			setError(err?.errors?.[0]?.message ?? err?.message ?? "Unknown error");
		} finally {
			setLoading(false);
		}
	};

	const handleGetRecentEvents = async (groupData, seq) => {
		const devices = [...getDevices(groupData, "input"), ...getDevices(groupData, "output")];
		const deviceIds = _.uniq(devices.map((device) => device.id));

		const responses = await Promise.all(
			deviceIds.flatMap((deviceId) => [
				API.graphql(
					graphqlOperation(liveEventByDeviceBasic, {
						deviceId,
						sortDirection: "DESC",
						limit: RECENT_EVENTS_LIMIT,
					}),
				),
				API.graphql(
					graphqlOperation(archiveEventByDeviceBasic, {
						deviceId,
						sortDirection: "DESC",
						limit: RECENT_EVENTS_LIMIT,
					}),
				),
			]),
		);

		const events = responses.flatMap((response) => {
			const result = response?.data?.liveEventByDevice ?? response?.data?.archiveEventByDevice;
			const isLive = Boolean(response?.data?.liveEventByDevice);
			return (result?.items ?? []).map((event) => ({ ...event, isLive }));
		});

		const deviceNamesById = new Map(devices.map((device) => [device.id, device.name]));
		const sortedEvents = _.orderBy(events, ["updatedAt"], ["desc"])
			.slice(0, RECENT_EVENTS_LIMIT)
			.map((event) => ({ ...event, deviceName: deviceNamesById.get(event.deviceId) ?? event.deviceId }));

		if (seq !== fetchSeq.current) return;
		setRecentEvents(sortedEvents);
	};

	//The live alarm atom (fed by the AppSync subscription) is the single source of truth -
	//mixing in fetched snapshots left stale Alarm pills when a re-fetch raced a clear
	const isInAlarm = (device) => activeAlarms?.some((alarm) => alarm?.device?.id === device.id) ?? false;

	const getAlarmPill = (device) => ({
		value: t(`alarm.group.details.state.${isInAlarm(device) ? "alarm" : "clear"}`),
		style: isInAlarm(device) ? "error" : "success",
	});

	const getOfflinePill = (device, deviceState) => {
		if (!deviceState.isOffline) return null;

		const lastSeen = deviceState.presence.lastSeenAtSeconds ?? device.updatedAt;
		return {
			value: t("device.state.presence.offline"),
			style: "grey",
			icon: "pi-wifi offlineIcon",
			tooltip: [
				deviceState.offlineSince &&
					`${t("device.state.offlineSince")}: ${getUiDateTime(deviceState.offlineSince)}`,
				`${t("device.state.lastSeen")}: ${getUiDateTime(lastSeen)}`,
			]
				.filter(Boolean)
				.join(". "),
		};
	};

	const renderInputDevice = (device) => {
		const meta = getDeviceMeta(device, inputDeviceOptions, "inputDevice");
		const deviceState = getOfflineState(device);
		const offlinePill = getOfflinePill(device, deviceState);

		return (
			<DeviceItem
				key={device.id}
				device={device}
				icon={meta.icon}
				typeLabel={[deviceState.presence.family, meta.typeLabel].filter(Boolean).join(" ")}
				offline={Boolean(offlinePill)}
				pills={[offlinePill, getAlarmPill(device)].filter(Boolean)}
			/>
		);
	};

	const renderOutputDevice = (device) => {
		const meta = getDeviceMeta(device, outputDeviceOptions, "outputDevice");
		const deviceState = getOfflineState(device);
		const offlinePill = getOfflinePill(device, deviceState);
		const valve = getDeviceValveSummary(device);
		let valvePill;
		if (valve.status === undefined || valve.status === null) {
			valvePill = { value: t("alarm.group.details.valve.unknown"), style: "blue1" };
		} else {
			const stateLabel = valve.status
				? t("alarm.group.details.valve.closed")
				: t("alarm.group.details.valve.open");
			valvePill = valve.disabled
				? { value: `${stateLabel} (${t("device.state.disabled")})`, style: "grey" }
				: { value: stateLabel, style: valve.status ? "error" : "blue1" };
		}

		return (
			<DeviceItem
				key={device.id}
				device={device}
				icon={meta.icon}
				typeLabel={[deviceState.presence.family, meta.typeLabel].filter(Boolean).join(" ")}
				offline={Boolean(offlinePill)}
				pills={[offlinePill, getAlarmPill(device), valvePill].filter(Boolean)}
			/>
		);
	};

	const renderDeviceList = (devices, renderDevice) =>
		devices.length > 0 ? (
			<div className="flexVert gapRow-small marginTop-small">{devices.map(renderDevice)}</div>
		) : (
			<p className="marginTop-small">{t("alarm.group.details.noDevices")}</p>
		);

	const renderEventDuration = (event) => {
		if (event.isLive) return t("alarm.group.details.events.ongoing");
		const seconds = event.duration ? event.duration : 0;
		return Duration.fromObject({ seconds }).rescale().toHuman({ unitDisplay: "narrow", listStyle: "narrow" }) || "---";
	};

	const PageBody = () => {
		if (loading)
			return (
				<div className="aSelf-center marginBottom-xlarge">
					<ProgressSpinner strokeWidth="4" />
				</div>
			);
		if (error) return <Note messageKey={`Error: ${error}`} icon="pi-exclamation-circle fontColour-error" />;
		if (!groupInfo)
			return (
				<Note messageKey={t("alarm.group.details.notFound")} icon="pi-exclamation-circle fontColour-error" />
			);

		const inputDevices = getDevices(groupInfo, "input");
		const outputDevices = getDevices(groupInfo, "output");
		const inputsInAlarm = inputDevices.some(isInAlarm);
		const outputsInAlarm = outputDevices.some(isInAlarm);

		return (
			<>
				{inputsInAlarm || outputsInAlarm ? (
					<div className="card errorBorder flex aItems-center gap-xsmall marginBottom-medium">
						<i className="pi pi-exclamation-triangle fontColour-error" />
						<span>{t("alarm.group.details.groupInAlarm")}</span>
					</div>
				) : (
					<div className="card successBorder flex aItems-center gap-xsmall marginBottom-medium">
						<i className="pi pi-check-circle fontColour-success" />
						<span>{t("alarm.group.details.groupClear")}</span>
					</div>
				)}
				<div className="grid columns-2 gap-medium">
					<div className={`card ${inputsInAlarm ? "errorBorder" : ""}`}>
						<h3>{`${t(
							useSensorActuatorLabels ? "alarm.group.details.sensors" : "alarm.group.details.inputs",
						)} (${inputDevices.length})`}</h3>
						{renderDeviceList(inputDevices, renderInputDevice)}
					</div>
					<div className={`card ${outputsInAlarm ? "errorBorder" : ""}`}>
						<h3>{`${t(
							useSensorActuatorLabels ? "alarm.group.details.actuators" : "alarm.group.details.outputs",
						)} (${outputDevices.length})`}</h3>
						{renderDeviceList(outputDevices, renderOutputDevice)}
					</div>
				</div>

				<div className="card marginTop-medium">
					<h3>{t("alarm.group.details.events.title")}</h3>
					{recentEvents.length > 0 ? (
						<div className="flexVert gapRow-xsmall marginTop-small">
							{recentEvents.map((event) => (
								<div key={event.id} className="flex aItems-center gap-small">
									<span className="fontSize-small">{getUiDateTime(event.updatedAt)}</span>
									<Link to={`${nav.device.base}/${event.deviceId}`}>{event.deviceName}</Link>
									<span>{t("events.eventType.eventType", { context: event.eventType })}</span>
									<span className="fontSize-small">{renderEventDuration(event)}</span>
								</div>
							))}
						</div>
					) : (
						<p className="marginTop-small">{t("alarm.group.details.events.none")}</p>
					)}
				</div>
			</>
		);
	};

	return (
		<>
			<PageHeader
				title={t("alarm.group.details.pageTitle", { groupId: groupInfo?.name ? groupInfo.name : groupId })}
				subtitle={t("alarm.group.details.pageSubtitle")}
				enableBack
			/>
			<main className="appMain">
				<div className="appMain-content">
					<div className="flex jContent-end gap-small">
						<Link
							to={`${nav.alarm.base}/${nav.alarm.group.base}/${nav.alarm.group.edit}/${groupId}`}
							className="button feature">
							{t("alarm.group.details.actions.edit")}
						</Link>
					</div>
					<PageBody />
				</div>
			</main>
		</>
	);
};

export default AlarmGroupDetails;
